import React from 'react';
import { PageLayout } from '@/shared/layout';
import { Button, Card, Avatar, Badge } from '@/components/ui';
import { useAuth } from '@/core/auth/AuthContext';
import { useCompany } from './hooks/useCompany';
import { useCompanyUsage } from './hooks/useCompanyUsage';
import { CompanyProfileCard } from './components/CompanyProfileCard';
import { CompanyUsageCard } from './components/CompanyUsageCard';
import { OnboardingChecklist } from './components/OnboardingChecklist';
import { usePermission } from '@/core/permissions/usePermission';
import { useNavigate } from 'react-router-dom';

export default function CompanyOverviewPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const companyId = profile?.company_id;
  const { can } = usePermission();

  const { company, isLoading: companyLoading } = useCompany(companyId || '');
  const { usage, isLoading: usageLoading } = useCompanyUsage(companyId || '');

  const breadcrumbs = [
    { label: 'Cấu hình', path: '/settings' },
    { label: 'Tổ chức' }
  ];

  return (
    <PageLayout
      title="Tổng quan Tổ chức"
      description="Quản lý thông tin định danh, tài nguyên và cấu hình hệ thống của doanh nghiệp."
      breadcrumbs={breadcrumbs}
      isLoading={companyLoading || usageLoading}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {company && (
              <CompanyProfileCard 
                company={company} 
                onEdit={() => navigate('/company/settings')} 
              />
            )}
            
            {usage && <CompanyUsageCard usage={usage} />}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate('/company/members')}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <i className="ri-group-line" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold">Thành viên</h4>
                    <p className="text-[10px] text-slate-500">Phân quyền & Mời</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate('/company/billing')}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                    <i className="ri-bank-card-line" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold">Gói dịch vụ</h4>
                    <p className="text-[10px] text-slate-500">Thanh toán & Hóa đơn</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate('/company/security')}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center text-lg group-hover:bg-orange-600 group-hover:text-white transition-colors">
                    <i className="ri-shield-keyhole-line" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold">Bảo mật</h4>
                    <p className="text-[10px] text-slate-500">Nhật ký & Truy cập</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <div className="space-y-6">
            <OnboardingChecklist 
              items={[
                { id: '1', label: 'Cập nhật Profile', description: 'Hoàn thiện thông tin liên hệ và Logo.', isCompleted: !!company?.logo_path },
                { id: '2', label: 'Mời thành viên', description: 'Thêm cộng sự vào không gian làm việc.', isCompleted: (usage?.users_count || 0) > 1 },
                { id: '3', label: 'Thiết lập dự án đầu tiên', description: 'Bắt đầu quản lý khối lượng thi công.', isCompleted: (usage?.projects_count || 0) > 0 },
              ]}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
